import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { Box, Modal } from "@mui/material";
import { toast } from "react-hot-toast";
import ModalCloseButton from "../../../components/Admin/ModalCloseButton";
import {
  useAddMenuItemMasterMutation,
  useDeleteMenuItemMasterMutation,
  useFetchAllMenuItemsMutation,
} from "../../../features/admin/appUserMenu/appUserMenuApi";

const emptyForm = { name: "", action: "", link: "", order_at: "", icon: null };

const addModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: 560,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 8,
  p: 3,
  outline: "none",
};

const QuickUpdateMenuItems = ({ onClose }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [iconPreview, setIconPreview] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);

  const [fetchMenuItems, { data: menuData, isLoading, error: fetchError }] =
    useFetchAllMenuItemsMutation();
  const [addMenuItemMaster, { data: addData, isLoading: isAdding, error: addError }] =
    useAddMenuItemMasterMutation();
  const [
    deleteMenuItemMaster,
    { data: deleteData, isLoading: isDeleting, error: deleteError },
  ] = useDeleteMenuItemMasterMutation();

  const loadItems = () =>
    fetchMenuItems({ token: sessionStorage.getItem("access_token") });

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    if (menuData?.body) setItems(menuData.body);
  }, [menuData]);

  useEffect(() => {
    if (fetchError) toast.error("Failed to load menu items");
  }, [fetchError]);

  useMemo(() => {
    if (addData) {
      toast.success("Menu item added");
      setAddModalOpen(false);
      setForm(emptyForm);
      setIconPreview(null);
      loadItems();
    }
    if (addError) {
      toast.error(addError?.data?.message || "Failed to add menu item");
    }
  }, [addData, addError]);

  useMemo(() => {
    if (deleteData) {
      toast.success("Menu item deleted");
      setDeletingItemId(null);
      loadItems();
    }
    if (deleteError) {
      toast.error("Failed to delete menu item");
      setDeletingItemId(null);
    }
  }, [deleteData, deleteError]);

  const filteredItems = [...(items || [])]
    .sort((a, b) => Number(a.order_at || 0) - Number(b.order_at || 0))
    .filter((item) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return `${item.name} ${item.action} ${item.link}`.toLowerCase().includes(q);
    });

  const onFormChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!form.icon) {
      setIconPreview(null);
      return;
    }

    const previewUrl = URL.createObjectURL(form.icon);
    setIconPreview(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [form.icon]);

  const handleAddModalClose = () => {
    setAddModalOpen(false);
    setForm(emptyForm);
    setIconPreview(null);
  };

  const handleAddItem = (e) => {
    e.preventDefault();
    if (!form.name || !form.action || !form.link || !form.order_at || !form.icon) {
      toast.error("All fields are required");
      return;
    }

    addMenuItemMaster({
      token: sessionStorage.getItem("access_token"),
      name: form.name.trim(),
      action: form.action.trim(),
      link: form.link.trim(),
      order_at: Number(form.order_at),
      icon: form.icon,
    });
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this menu item?")) return;
    setDeletingItemId(id);
    deleteMenuItemMaster({ token: sessionStorage.getItem("access_token"), id });
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-3 pb-2 border-bottom">
        <div className="pr-3">
          <h5 className="mb-1 font-weight-bold text-primary">Menu Items</h5>
          <p className="text-muted small mb-0">
            Master list used when configuring client menus. Add new items here to
            make them available everywhere.
          </p>
        </div>
        <ModalCloseButton onClose={onClose} />
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: 320 }}
          placeholder="Search items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-primary btn-sm px-3"
          onClick={() => setAddModalOpen(true)}
        >
          + Add Item
        </button>
      </div>

      <div
        className="border rounded p-2 bg-white"
        style={{ minHeight: 300, maxHeight: "52vh", overflowY: "auto", overflowX: "hidden" }}
      >
        {isLoading && (
          <div className="text-center py-5 text-muted">Loading items...</div>
        )}
        {!isLoading && filteredItems.length === 0 && (
          <div className="text-center py-5 text-muted">
            {search ? "No items match your search." : "No menu items yet."}
          </div>
        )}
        {!isLoading && filteredItems.length > 0 && (
          <div className="row">
            {filteredItems.map((item, idx) => (
              <div key={item.id} className="col-12 col-sm-6 col-lg-3 mb-3">
                <div
                  className="card h-100 shadow-sm border-0"
                  style={{ minHeight: 102, overflow: "hidden" }}
                >
                  <div className="card-body py-1 px-2 d-flex flex-column">
                    <div className="d-flex justify-content-end mb-1">
                      <span className="badge badge-primary badge-pill">#{idx + 1}</span>
                    </div>

                    <div className="d-flex align-items-start justify-content-between">
                      <div className="d-flex align-items-start flex-grow-1" style={{ minWidth: 0 }}>
                        <img
                          src={item.icon}
                          alt={item.name}
                          style={{
                            width: 30,
                            height: 30,
                            objectFit: "contain",
                            marginRight: 8,
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <h6
                            className="card-title mb-1 font-weight-bold text-truncate"
                            style={{ fontSize: "0.86rem" }}
                          >
                            {item.name}
                          </h6>
                          <p
                            className="card-text text-muted mb-0 text-truncate"
                            style={{ lineHeight: "1.12", fontSize: "0.70rem" }}
                          >
                            {item.action}
                          </p>
                          <p
                            className="card-text text-muted mb-0 text-truncate"
                            style={{ lineHeight: "1.12", fontSize: "0.70rem" }}
                          >
                            {item.link ? `/${item.link}` : "/"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-danger"
                        aria-label={`Delete ${item.name}`}
                        onClick={() => handleDelete(item.id)}
                        disabled={isDeleting && deletingItemId === item.id}
                      >
                        {isDeleting && deletingItemId === item.id ? (
                          <span className="spinner-border spinner-border-sm text-danger" />
                        ) : (
                          <AiOutlineDelete size={15} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-muted small mt-2">{filteredItems.length} items shown</div>

      <Modal open={addModalOpen} onClose={handleAddModalClose}>
        <Box sx={addModalStyle}>
          <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
            <h6 className="mb-0 font-weight-bold">Add Menu Item</h6>
            <ModalCloseButton onClose={handleAddModalClose} />
          </div>

          <form onSubmit={handleAddItem}>
            <div className="row">
              <div className="col-md-6 form-group mb-3">
                <label className="small font-weight-bold d-block mb-1">Name *</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => onFormChange("name", e.target.value)}
                  placeholder="Profile"
                />
              </div>
              <div className="col-md-6 form-group mb-3">
                <label className="small font-weight-bold d-block mb-1">Action *</label>
                <input
                  className="form-control"
                  value={form.action}
                  onChange={(e) => onFormChange("action", e.target.value)}
                  placeholder="profile"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 form-group mb-3">
                <label className="small font-weight-bold d-block mb-1">Link *</label>
                <input
                  className="form-control"
                  value={form.link}
                  onChange={(e) => onFormChange("link", e.target.value)}
                  placeholder="profile"
                />
              </div>
              <div className="col-md-6 form-group mb-3">
                <label className="small font-weight-bold d-block mb-1">Order *</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.order_at}
                  onChange={(e) => onFormChange("order_at", e.target.value)}
                  min={1}
                />
              </div>
            </div>

            <div className="form-group mb-3">
              <label className="small font-weight-bold d-block mb-2">Icon *</label>
              <div className="border rounded p-3 bg-light">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <label htmlFor="menu-item-icon" className="btn btn-outline-primary btn-sm mb-0">
                      Choose Icon
                    </label>
                    <input
                      id="menu-item-icon"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        onFormChange("icon", file);
                      }}
                      className="d-none"
                    />
                    <span className="small text-muted text-truncate" style={{ maxWidth: 190 }}>
                      {form.icon?.name || "No file selected"}
                    </span>
                  </div>
                  <div
                    className="border rounded bg-white d-flex align-items-center justify-content-center ml-2"
                    style={{ width: 64, height: 64, flexShrink: 0, overflow: "hidden" }}
                  >
                    {iconPreview ? (
                      <img
                        src={iconPreview}
                        alt="Icon preview"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <span className="small text-muted">Preview</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 pt-2 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleAddModalClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm px-4" disabled={isAdding}>
                {isAdding ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default QuickUpdateMenuItems;
